import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Building2, Users, FileText, Activity, 
  TrendingUp, RefreshCw, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ title, value, icon: Icon, color = "teal", subValue }) {
  const colors = {
    teal: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };

  return (
    <div className={`p-5 rounded-xl border ${colors[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colors[color].split(' ')[0]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function PlanDistribution({ data }) {
  const plans = [
    { key: "free", label: "Free", color: "bg-slate-500" },
    { key: "basic", label: "Basic", color: "bg-blue-500" },
    { key: "standard", label: "Standard", color: "bg-purple-500" },
    { key: "pro", label: "Pro", color: "bg-amber-500" },
    { key: "enterprise", label: "Enterprise", color: "bg-emerald-500" },
  ];
  
  const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
      <h3 className="text-lg font-semibold text-white mb-4">Distribución por Plan</h3>
      <div className="space-y-3">
        {plans.map(({ key, label, color }) => {
          const count = data[key] || 0;
          const percent = Math.round((count / total) * 100);
          return (
            <div key={key}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">{label}</span>
                <span className="text-slate-400">{count} ({percent}%)</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${color} rounded-full transition-all duration-500`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/superadmin/metrics`, { withCredentials: true });
      setMetrics(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Error loading metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-300">{error}</p>
        <button 
          onClick={fetchMetrics}
          className="mt-4 px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard SuperAdmin</h1>
          <p className="text-slate-400 mt-1">Vista general de la plataforma Notalia</p>
        </div>
        <button
          onClick={fetchMetrics}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Organizaciones"
          value={metrics.totals.organizations}
          icon={Building2}
          color="amber"
          subValue={`${metrics.last_7_days.new_organizations} nuevas esta semana`}
        />
        <StatCard
          title="Usuarios"
          value={metrics.totals.users}
          icon={Users}
          color="teal"
          subValue={`${metrics.last_7_days.new_users} nuevos esta semana`}
        />
        <StatCard
          title="Clientes"
          value={metrics.totals.customers}
          icon={FileText}
          color="purple"
          subValue={`${metrics.last_7_days.new_customers} nuevos esta semana`}
        />
        <StatCard
          title="Operaciones"
          value={metrics.totals.operations}
          icon={Activity}
          color="blue"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PlanDistribution data={metrics.organizations_by_plan} />

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Estado de Organizaciones</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg">
              <span className="text-emerald-300">Activas</span>
              <span className="text-2xl font-bold text-emerald-400">
                {metrics.organizations_by_status.active}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg">
              <span className="text-amber-300">Trial</span>
              <span className="text-2xl font-bold text-amber-400">
                {metrics.organizations_by_status.trial}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
              <span className="text-red-300">Suspendidas</span>
              <span className="text-2xl font-bold text-red-400">
                {metrics.organizations_by_status.suspended}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Uso Mensual Global</h3>
          <div className="space-y-3">
            {[
              { label: "Onboardings", value: metrics.monthly_usage_totals.onboardings },
              { label: "Screenings", value: metrics.monthly_usage_totals.screenings },
              { label: "PDFs", value: metrics.monthly_usage_totals.pdfs },
              { label: "OCR", value: metrics.monthly_usage_totals.ocr },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between p-2 border-b border-slate-800 last:border-0">
                <span className="text-slate-400">{label}</span>
                <span className="text-white font-semibold">{value.toLocaleString()}</span>
              </div>
            ))}
            <div className="pt-2 mt-2 border-t border-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Storage Total</span>
                <span className="text-white font-semibold">
                  {(metrics.monthly_usage_totals.storage_mb / 1024).toFixed(2)} GB
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-600 text-right">
        Actualizado: {new Date(metrics.generated_at).toLocaleString("es-MX")}
      </p>
    </div>
  );
}
